/**
 * METRIC CARD GRID
 *
 * Responsive grid layout for displaying multiple MetricCards.
 * Automatically arranges cards in a row with wrap behavior.
 */

import React from 'react'
import { token } from '@atlaskit/tokens'
import MetricCard from './MetricCard'

/**
 * MetricCardGrid Component
 *
 * @param {object} props
 * @param {Array} props.metrics - Array of metric objects:
 *   { value, label, icon?, trend?, trendValue?, color? }
 * @param {number} props.columns - Number of columns (default: auto-fit based on content)
 * @param {string} props.gap - Gap between cards (default: '16px')
 * @param {boolean} props.compact - Whether to use compact card styling (default: false)
 */
const MetricCardGrid = ({
  metrics = [],
  columns,
  gap = '16px',
  compact = false
}) => {
  // Calculate grid template based on columns prop or auto-fit
  const getGridTemplate = () => {
    if (columns) {
      return `repeat(${columns}, 1fr)`
    }
    // Auto-fit with minimum width
    return `repeat(auto-fit, minmax(${compact ? '100px' : '140px'}, 1fr))`
  }

  if (!metrics || metrics.length === 0) {
    return null
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: getGridTemplate(),
        gap: gap,
        width: '100%'
      }}
    >
      {metrics.map((metric, index) => (
        <MetricCard
          key={index}
          value={metric.value}
          label={metric.label}
          icon={metric.icon}
          trend={metric.trend}
          trendValue={metric.trendValue}
          color={metric.color}
          compact={compact}
        />
      ))}
    </div>
  )
}

/**
 * Helper function to create metrics array from report summary data
 *
 * @param {object} summary - ExecutionSummaryResponse object
 * @returns {Array} Array of metric objects for MetricCardGrid
 */
export const createMetricsFromSummary = (summary) => {
  if (!summary) {
    return []
  }

  return [
    {
      value: summary.totalTestCases,
      label: 'Total Cases'
    },
    {
      value: summary.executedTestCases,
      label: 'Executed'
    },
    {
      value: summary.notExecutedTestCases,
      label: 'Not Executed'
    },
    {
      value: summary.totalRuns,
      label: 'Test Runs'
    },
    {
      value: summary.totalExecutions,
      label: 'Total Execs'
    }
  ]
}

/**
 * Helper function to format date for display
 *
 * @param {string|null} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatLastExecutionDate = (dateString) => {
  if (!dateString) {
    return 'Never'
  }

  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    // If within last 24 hours, show relative time
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        if (diffMinutes < 1) return 'Just now'
        return `${diffMinutes}m ago`
      }
      return `${diffHours}h ago`
    }

    // If within last 7 days, show days ago
    if (diffDays < 7) {
      return `${diffDays}d ago`
    }

    // Otherwise show formatted date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  } catch (error) {
    return 'Unknown'
  }
}

export default MetricCardGrid
